import { TriangleAlert } from 'lucide-react-native';
import { useState, type Ref } from 'react';
import { twJoin } from 'tailwind-merge';

import type { CheckItem, MustFixAcknowledgement } from '../../model/types';
import { REPLY_DRAFT_COPY } from '../copy';
import type { HeadingNode } from '../shared/focusHeading';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type Resolution = 'added' | 'acknowledged';

/** A final-check item names the composed text, not a suggestion, so it can only be acknowledged. */
const FINAL_CHECK_PREFIX = 'final:';

type MustFixListProps = {
  items: CheckItem[];
  composedHash: string;
  /** Puts must-fix suggestions in the reply; the next "use draft" checks the changed text again. */
  onAdd: (segmentIds: string[]) => void;
  onContinue: (acknowledgement: MustFixAcknowledgement) => void;
  onBack: () => void;
  headingRef?: Ref<HeadingNode>;
};

/**
 * What held the hand-off. Every item needs an explicit answer: added to the
 * reply, or confirmed as checked. Leaving the list is not an answer, so going
 * back hands nothing off.
 *
 * The retry carries must-fix suggestions by id and the final check by the hash of the
 * text it ran on, since the model renames its findings between runs. Adding an
 * item changes the text, so the server checks it again.
 */
export function MustFixList({
  items,
  composedHash,
  onAdd,
  onContinue,
  onBack,
  headingRef,
}: MustFixListProps) {
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({});

  const resolve = (segmentId: string, resolution: Resolution) =>
    setResolutions((current) =>
      current[segmentId] === resolution
        ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== segmentId))
        : { ...current, [segmentId]: resolution }
    );

  const isComplete = items.every((item) => resolutions[item.segmentId]);

  const continueWith = () => {
    const added = items
      .filter((item) => resolutions[item.segmentId] === 'added')
      .map((item) => item.segmentId);

    if (added.length > 0) {
      onAdd(added);
    }

    const acknowledgedKeys = items
      .filter(
        (item) =>
          resolutions[item.segmentId] === 'acknowledged' &&
          !item.segmentId.startsWith(FINAL_CHECK_PREFIX)
      )
      .map((item) => item.segmentId);

    const hasFinalCheck = items.some((item) => item.segmentId.startsWith(FINAL_CHECK_PREFIX));

    onContinue({
      acknowledgedKeys,
      ...(hasFinalCheck ? { acknowledgedFinalCheck: composedHash } : {}),
    });
  };

  return (
    <VStack className="gap-4" testID="reply-draft-must-fix">
      <HStack className="items-center gap-2">
        <Icon as={TriangleAlert} size="md" className="text-error-700" />
        <Heading ref={headingRef} size="h2-md" className="flex-1 font-semibold text-typography-900">
          {REPLY_DRAFT_COPY.mustFixHeading}
        </Heading>
      </HStack>

      <Text size="sm" className="text-typography-700">
        {REPLY_DRAFT_COPY.mustFixIntro}
      </Text>

      {items.map((item) => {
        const resolution = resolutions[item.segmentId];

        const canAdd = !item.segmentId.startsWith(FINAL_CHECK_PREFIX);

        return (
          <VStack
            key={item.segmentId}
            className="gap-2 rounded-lg border-2 border-error-600 bg-background-error p-3"
            testID={`reply-draft-must-fix-${item.segmentId}`}>
            <Text size="xs" className="font-bold uppercase text-error-700">
              {REPLY_DRAFT_COPY.mustFix}
            </Text>
            <Text size="sm" className="text-typography-900">
              {item.label}
            </Text>

            <HStack className="flex-wrap gap-2">
              {canAdd ? (
                <Pressable
                  onPress={() => resolve(item.segmentId, 'added')}
                  testID={`reply-draft-must-fix-add-${item.segmentId}`}
                  className={twJoin(
                    'min-h-12 justify-center rounded-full border px-4 web:cursor-pointer',
                    resolution === 'added'
                      ? 'border-secondary-600 bg-secondary-600'
                      : 'border-outline-500 bg-background-0'
                  )}
                  {...getAccessibilityProps({
                    label: REPLY_DRAFT_COPY.mustFixAddLabel(item.label),
                  })}
                  accessibilityRole="checkbox"
                  role="checkbox"
                  accessibilityState={{ checked: resolution === 'added' }}
                  aria-checked={resolution === 'added'}>
                  <Text
                    size="sm"
                    className={
                      resolution === 'added'
                        ? 'font-bold text-typography-0'
                        : 'font-medium text-typography-900'
                    }>
                    {REPLY_DRAFT_COPY.mustFixAdd}
                  </Text>
                </Pressable>
              ) : null}

              <Pressable
                onPress={() => resolve(item.segmentId, 'acknowledged')}
                testID={`reply-draft-must-fix-ack-${item.segmentId}`}
                className={twJoin(
                  'min-h-12 justify-center rounded-full border px-4 web:cursor-pointer',
                  resolution === 'acknowledged'
                    ? 'border-secondary-600 bg-secondary-600'
                    : 'border-outline-500 bg-background-0'
                )}
                {...getAccessibilityProps({
                  label: REPLY_DRAFT_COPY.mustFixAcknowledgeLabel(item.label),
                })}
                accessibilityRole="checkbox"
                role="checkbox"
                accessibilityState={{ checked: resolution === 'acknowledged' }}
                aria-checked={resolution === 'acknowledged'}>
                <Text
                  size="sm"
                  className={
                    resolution === 'acknowledged'
                      ? 'font-bold text-typography-0'
                      : 'font-medium text-typography-900'
                  }>
                  {REPLY_DRAFT_COPY.mustFixAcknowledge}
                </Text>
              </Pressable>
            </HStack>
          </VStack>
        );
      })}

      <HStack className="gap-3">
        <Button
          size="lg"
          variant="outline"
          action="secondary"
          className="min-h-12 flex-1"
          onPress={onBack}
          testID="reply-draft-must-fix-back"
          {...getAccessibilityProps({ role: 'button', label: REPLY_DRAFT_COPY.mustFixBack })}>
          <ButtonText className="text-secondary-700">{REPLY_DRAFT_COPY.mustFixBack}</ButtonText>
        </Button>

        <Button
          size="lg"
          action="primary"
          className="min-h-12 flex-1"
          isDisabled={!isComplete}
          onPress={continueWith}
          testID="reply-draft-must-fix-continue"
          {...getAccessibilityProps({ role: 'button', label: REPLY_DRAFT_COPY.mustFixContinue })}>
          <ButtonText>{REPLY_DRAFT_COPY.mustFixContinue}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
